// Client-side (browser) image compression — WhatsApp-style auto optimize before upload.
// Resizes, adaptive quality, strips EXIF/GPS (canvas re-encode), fixes orientation, WebP w/ JPEG fallback.
// Storage-provider agnostic: returns a compressed File that the existing upload endpoint pushes to storage.

const PRESETS = {
  FOOD: { maxDim: 1280, target: 500 * 1024, hardCap: 750 * 1024, minQuality: 0.5 },
  PROGRESS: { maxDim: 1600, target: 800 * 1024, hardCap: 1024 * 1024, minQuality: 0.55 },
  EDU: { maxDim: 1600, target: 800 * 1024, hardCap: 1024 * 1024, minQuality: 0.62 },
  // Documents (payment proofs, invoices): keep text crisp — gentle quality floor.
  DOC: { maxDim: 1600, target: 600 * 1024, hardCap: 1200 * 1024, minQuality: 0.72 },
  // Feed thumbnail: small variant shown inline in the Wall; full image loaded on zoom.
  THUMB: { maxDim: 480, quality: 0.68 },
};

const ALLOWED_MIME = /^image\/(jpe?g|png|webp|heic|heif)$/i;

function supportsWebp() {
  try {
    return document.createElement("canvas").toDataURL("image/webp").indexOf("data:image/webp") === 0;
  } catch {
    return false;
  }
}

// Load into an orientation-corrected source. createImageBitmap applies EXIF orientation
// and drops metadata; <img> fallback also auto-orients on modern browsers.
async function loadOriented(file) {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      /* fall through to <img> */
    }
  }
  return await new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("decode_failed"));
    };
    img.src = url;
  });
}

function dimsOf(src) {
  return { w: src.width || src.naturalWidth || 0, h: src.height || src.naturalHeight || 0 };
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), type, quality));
}

async function encodeAt(source, maxDim, type, quality) {
  const { w, h } = dimsOf(source);
  if (!w || !h) return null;
  const scale = Math.min(1, maxDim / Math.max(w, h));
  const tw = Math.max(1, Math.round(w * scale));
  const th = Math.max(1, Math.round(h * scale));
  const canvas = document.createElement("canvas");
  canvas.width = tw;
  canvas.height = th;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(source, 0, 0, tw, th);
  const blob = await canvasToBlob(canvas, type, quality);
  return blob ? { blob, w: tw, h: th } : null;
}

function ext(type) {
  return type === "image/webp" ? "webp" : "jpg";
}

function buildResult(originalFile, best, type) {
  const name = (originalFile.name || "photo").replace(/\.[^.]+$/, "") + "." + ext(type);
  const outFile = new File([best.blob], name, { type });
  return {
    file: outFile,
    width: best.w,
    height: best.h,
    originalSize: originalFile.size,
    compressedSize: best.blob.size,
    ratio: originalFile.size ? Math.max(0, 1 - best.blob.size / originalFile.size) : 0,
  };
}

// Compress `file` using a named preset. onStage("optimizing") fired while processing.
// Throws Error("mime_invalid" | "decode_failed" | "too_large") on failure — caller must NOT
// upload the original in that case.
export async function compressImage(file, presetName = "FOOD", onStage, opts = {}) {
  if (!file || !ALLOWED_MIME.test(file.type || "")) throw new Error("mime_invalid");
  const preset = PRESETS[presetName] || PRESETS.FOOD;
  if (onStage) onStage("optimizing");

  let source;
  try {
    source = await loadOriented(file);
  } catch {
    throw new Error("decode_failed");
  }

  const type = supportsWebp() ? "image/webp" : "image/jpeg";
  // Descending quality steps from 0.85 down to the preset floor (never below minQuality).
  const qualities = [];
  for (let q = 0.85; q > preset.minQuality + 0.001; q -= 0.08) qualities.push(Math.round(q * 100) / 100);
  qualities.push(preset.minQuality);
  const dims = [preset.maxDim, Math.round(preset.maxDim * 0.82), Math.round(preset.maxDim * 0.66)];

  let best = null;
  try {
    outer: for (const d of dims) {
      for (const q of qualities) {
        const out = await encodeAt(source, d, type, q);
        if (!out) continue;
        if (!best || out.blob.size < best.blob.size) best = out;
        if (out.blob.size <= preset.target) break outer;
      }
    }
    if (!best || best.blob.size > preset.hardCap) throw new Error("too_large");
    const result = buildResult(file, best, type);
    if (opts.thumbnail) {
      const tp = PRESETS.THUMB;
      const tout = await encodeAt(source, tp.maxDim, type, tp.quality);
      if (tout) {
        const tname = (file.name || "photo").replace(/\.[^.]+$/, "") + "_thumb." + ext(type);
        result.thumbnail = { file: new File([tout.blob], tname, { type }), width: tout.w, height: tout.h };
      }
    }
    return result;
  } finally {
    if (source && typeof source.close === "function") source.close();
  }
}

export { PRESETS };
